import {defineSlotRecipe} from '@chakra-ui/react3'

const chakraDrawerSlotRecipe = defineSlotRecipe({
    slots: [
        'trigger',
        'backdrop',
        'positioner',
        'content',
        'title',
        'description',
        'closeTrigger',
        'header',
        'body',
        'footer',
        'backdrop'
    ],
    className: 'chakra-drawer',
    base: {
        backdrop: {
            bg: 'blackAlpha.500',
            pos: 'fixed',
            insetInlineStart: 0,
            top: 0,
            w: '100vw',
            h: '100dvh',
            zIndex: 'overlay',
            _open: {
                animationName: 'fade-in',
                animationDuration: 'slow'
            },
            _closed: {
                animationName: 'fade-out',
                animationDuration: 'moderate'
            }
        },
        positioner: {
            display: 'flex',
            width: '100vw',
            height: '100dvh',
            position: 'fixed',
            insetInlineStart: 0,
            top: 0,
            zIndex: 'modal',
            overscrollBehaviorY: 'none'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            width: '100%',
            outline: 0,
            zIndex: 'modal',
            textStyle: 'sm',
            maxH: '100dvh',
            color: 'inherit',
            bg: 'bg.panel',
            boxShadow: 'lg',
            _open: {
                animationDuration: 'slowest',
                animationTimingFunction: 'ease-in-smooth'
            },
            _closed: {
                animationDuration: 'slower',
                animationTimingFunction: 'ease-in-smooth'
            }
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '2',
            flex: 0,
            px: '6',
            pt: '6',
            pb: '4'
        },
        body: {
            px: '6',
            py: '2',
            flex: '1',
            overflow: 'auto'
        },
        footer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '3',
            px: '6',
            pt: '2',
            pb: '4'
        },
        title: {
            flex: '1',
            textStyle: 'lg',
            fontWeight: 'semibold'
        },
        description: {
            color: 'fg.muted'
        },
        closeTrigger: {
            pos: 'absolute',
            top: '3',
            insetEnd: '2'
        }
    },
    variants: {
        placement: {
            start: {
                positioner: {
                    justifyContent: 'flex-start',
                    alignItems: 'stretch'
                },
                content: {
                    _open: {
                        animationName: {
                            base: 'slide-from-left-full, fade-in',
                            _rtl: 'slide-from-right-full, fade-in'
                        }
                    },
                    _closed: {
                        animationName: {
                            base: 'slide-to-left-full, fade-out',
                            _rtl: 'slide-to-right-full, fade-out'
                        }
                    }
                }
            },
            end: {
                positioner: {
                    justifyContent: 'flex-end',
                    alignItems: 'stretch'
                },
                content: {
                    _open: {
                        animationName: {
                            base: 'slide-from-right-full, fade-in',
                            _rtl: 'slide-from-left-full, fade-in'
                        }
                    },
                    _closed: {
                        animationName: {
                            base: 'slide-to-right-full, fade-out',
                            _rtl: 'slide-to-left-full, fade-out'
                        }
                    }
                }
            },
            top: {
                positioner: {
                    justifyContent: 'stretch',
                    alignItems: 'flex-start'
                },
                content: {
                    maxW: '100%',
                    _open: {
                        animationName: 'slide-from-top-full, fade-in'
                    },
                    _closed: {
                        animationName: 'slide-to-top-full, fade-out'
                    }
                }
            },
            bottom: {
                positioner: {
                    justifyContent: 'stretch',
                    alignItems: 'flex-end'
                },
                content: {
                    maxW: '100%',
                    _open: {
                        animationName: 'slide-from-bottom-full, fade-in'
                    },
                    _closed: {
                        animationName: 'slide-to-bottom-full, fade-out'
                    }
                }
            }
        },
        contained: {
            true: {
                positioner: {
                    padding: '4'
                },
                content: {
                    borderRadius: 'l3'
                }
            }
        }
    },
    defaultVariants: {
        placement: 'end'
    }
})

export const drawerSlotRecipe = defineSlotRecipe({
    ...chakraDrawerSlotRecipe,
    base: {
        backdrop: {
            backgroundColor: 'background.overlay.medium',
            position: 'fixed',
            insetInlineStart: 0,
            top: 0,
            width: '100vw',
            height: '100dvh',
            zIndex: 'overlay',
            _open: {
                animationName: 'fade-in',
                animationDuration: 'slow'
            },
            _closed: {
                animationName: 'fade-out',
                animationDuration: 'moderate'
            }
        },
        positioner: {
            display: 'flex',
            width: '100vw',
            height: '100dvh',
            position: 'fixed',
            insetInlineStart: 0,
            insetBlockStart: 0,
            zIndex: 'modal',
            overscrollBehaviorY: 'none'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            width: '100%',
            maxW: '448px',
            outline: 0,
            zIndex: 'modal',
            textStyle: 'body.small',
            maxH: '100dvh',
            color: 'inherit',
            backgroundColor: 'background.section.default',
            overflow: 'auto',
            _before: {
                content: '""',
                display: 'block',
                position: 'sticky',
                insetBlockStart: 0,
                zIndex: '1',
                pointerEvents: 'none',
                width: '100%',
                height: '68px',
                flexShrink: 0,
                marginBlockEnd: '-68px',
                background:
                    'linear-gradient(180deg, rgba(238, 233, 226, 0.60) 0%, rgba(238, 233, 226, 0.00) 100%)',
                backdropFilter: 'blur(0px)',
                opacity: 0,
                transition: 'opacity 0.3s ease-in'
            },
            '&[data-drawer-scrolled="true"]::before': {
                opacity: 1
            },
            _open: {
                animationDuration: 'slowest',
                animationTimingFunction: 'ease-in-smooth'
            },
            _closed: {
                animationDuration: 'slower',
                animationTimingFunction: 'ease-in-smooth'
            }
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '2',
            flex: 0,
            paddingInline: 'spacing-24',
            paddingBlockStart: 'spacing-24',
            paddingBlockEnd: 'spacing-16'
        },
        body: {
            flex: '1'
        },
        footer: {
            position: 'sticky',
            zIndex: 'sticky',
            insetBlockEnd: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 'spacing-16',
            py: 'spacing-20',
            px: 'spacing-24',
            borderTop: 'sm',
            borderColor: 'border.medium',
            backdropFilter: 'blur(8px)'
        },
        title: {
            flex: '1',
            color: 'text.primary',
            textStyle: 'display.small'
        },
        description: {
            color: 'text.secondary'
        },
        closeTrigger: {
            position: 'sticky',
            alignSelf: 'flex-end',
            insetBlockStart: 'spacing-16',
            insetInlineEnd: 'spacing-16',
            marginBlockEnd: '-36px', // TODO: how to clear the space of the element in a generic way?
            zIndex: '3'
        }
    },
    variants: {
        variant: {
            configurator: {
                positioner: {
                    paddingBlockStart: {base: 'spacing-32', xs: '0'}
                },
                content: {
                    borderRadius: {base: '{radii.l} {radii.l} 0 0', xs: 'none'},
                    backgroundColor: 'primary.base.white'
                },
                body: {
                    paddingInline: '0'
                },
                footer: {
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                    backgroundColor: 'primary.base.white',
                    backdropFilter: 'none'
                }
            }
        },
        placement: chakraDrawerSlotRecipe.variants.placement
    }
})
